import { SectionLabel } from "../ui.js";
import { useState } from "react";
import type { Snapshot, Innings, RecordedDelivery } from "./scoreTypes.js";
import { describe as line, overCall, targetCall, resultCall, type Lang } from "../commentary.js";
import { situational, type Situation } from "../situation.js";
import { Manhattan, RunRate } from "./Charts.js";
import { bankOf } from "../voice.js";

/**
 * The full card and the over-by-over.
 *
 * Both read from the snapshot the app already holds, so they work offline and
 * cost nothing extra to show. Shared by the scorer's stats tab and the viewer.
 */

export function Scorecard({ snap, name }: { snap: Snapshot; name: (id: string) => string }) {
  const per = snap.meta.ruleset.ballsPerOver;
  return (
    <div className="px-3 pt-4 split:px-6">
      {snap.state.innings.map((inn, i) => (
        <InningsCard key={i} snap={snap} inn={inn} per={per} name={name} />
      ))}
      {snap.state.innings.length === 0 && (
        <p className="px-3 py-10 text-center text-body text-faint">Nothing scored yet.</p>
      )}
    </div>
  );
}

function InningsCard({ snap, inn, per, name }: {
  snap: Snapshot; inn: Innings; per: number; name: (id: string) => string;
}) {
  // The innings in progress opens; finished ones collapse. Two full cards on a
  // phone is a lot of scrolling to reach the one you care about.
  const [open, setOpen] = useState(!inn.complete);
  const team = snap.meta.teams[inn.battingTeamId];
  const batted = inn.battingOrder.filter((p) => inn.batters[p]);
  const extras = inn.extras;
  const extraTotal = extras.wides + extras.noBalls + extras.byes + extras.legByes + extras.penalty;
  const bowlers = Object.values(inn.bowlers).filter((b) => b.legalBalls > 0);

  return (
    <div className="mb-5 overflow-hidden rounded-xl border border-line bg-panel">
      <button onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-3 bg-raise px-4 py-3 text-left">
        <b className="font-num text-sub font-semibold">{team?.name}</b>
        <span className="ml-auto font-mono text-lead font-semibold">
          {inn.totalRuns}/{inn.wickets}
          <span className="ml-2 font-normal text-faint">
            ({Math.floor(inn.legalBalls / per)}.{inn.legalBalls % per})
          </span>
        </span>
        <span className={`text-body text-faint transition-transform ${open ? "rotate-90" : ""}`}>›</span>
      </button>

      {open && (<>
      <table className="w-full text-left text-body">
        <thead>
          <tr className="border-b border-line font-num text-meta uppercase tracking-wider text-faint">
            <th className="px-4 py-2">Batter</th>
            <th className="py-2 text-right">R</th>
            <th className="py-2 text-right">B</th>
            <th className="hidden py-2 text-right split:table-cell">4s</th>
            <th className="hidden py-2 text-right split:table-cell">6s</th>
            <th className="py-2 pr-4 text-right">SR</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {batted.map((pid) => {
            const b = inn.batters[pid]!;
            const sr = b.ballsFaced ? (b.runs / b.ballsFaced) * 100 : 0;
            const atCrease = inn.atEnd.END_A === pid || inn.atEnd.END_B === pid;
            return (
              <tr key={pid} className="border-b border-line/60 last:border-b-0">
                <td className="px-4 py-2.5 font-sans">
                  {name(pid)}
                  <span className="ml-1.5 font-mono text-label text-faint">
                    {b.out ? how(b.dismissal?.kind) : atCrease ? "not out" : ""}
                  </span>
                </td>
                <td className="py-2.5 text-right font-semibold">{b.runs}</td>
                <td className="py-2.5 text-right text-faint">{b.ballsFaced}</td>
                <td className="hidden py-2.5 text-right text-faint split:table-cell">{b.fours}</td>
                <td className="hidden py-2.5 text-right text-faint split:table-cell">{b.sixes}</td>
                <td className="py-2.5 pr-4 text-right text-faint">{sr.toFixed(1)}</td>
              </tr>
            );
          })}
          <tr className="border-t border-line">
            <td className="px-4 py-2.5 font-sans text-faint">Extras</td>
            <td className="py-2.5 text-right font-semibold">{extraTotal}</td>
            <td colSpan={4} className="py-2.5 pr-4 text-right text-meta text-faint">
              {extras.byes}b · {extras.legByes}lb · {extras.wides}w · {extras.noBalls}nb
            </td>
          </tr>
          {inn.penaltyRuns !== 0 && (
            <tr className="border-t border-line">
              <td className="px-4 py-2.5 font-sans text-leather">Wicket penalty</td>
              <td colSpan={5} className="py-2.5 pr-4 text-right font-semibold text-leather">
                {inn.penaltyRuns}
              </td>
            </tr>
          )}
          {inn.bonusRuns > 0 && (
            <tr className="border-t border-line">
              <td className="px-4 py-2.5 font-sans text-crease">Bonus zones</td>
              <td colSpan={5} className="py-2.5 pr-4 text-right font-semibold text-crease">
                +{inn.bonusRuns}
              </td>
            </tr>
          )}
          <tr className="border-t-2 border-board bg-raise">
            <td className="px-4 py-3 font-sans font-semibold">Total</td>
            <td colSpan={5} className="py-3 pr-4 text-right font-semibold">
              {inn.totalRuns}/{inn.wickets} ({Math.floor(inn.legalBalls / per)}.{inn.legalBalls % per})
            </td>
          </tr>
        </tbody>
      </table>

      {bowlers.length > 0 && (
        <table className="w-full border-t border-line text-left text-body">
          <thead>
            <tr className="border-b border-line font-num text-meta uppercase tracking-wider text-faint">
              <th className="px-4 py-2">Bowler</th>
              <th className="py-2 text-right">O</th>
              <th className="hidden py-2 text-right split:table-cell">M</th>
              <th className="py-2 text-right">R</th>
              <th className="py-2 text-right">W</th>
              <th className="py-2 pr-4 text-right">Econ</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {bowlers.map((b) => {
              const econ = b.legalBalls ? b.runsConceded / (b.legalBalls / per) : 0;
              return (
                <tr key={b.playerId} className="border-b border-line/60 last:border-b-0">
                  <td className="px-4 py-2.5 font-sans">{name(b.playerId)}</td>
                  <td className="py-2.5 text-right">{Math.floor(b.legalBalls / per)}.{b.legalBalls % per}</td>
                  <td className="hidden py-2.5 text-right text-faint split:table-cell">{b.maidens}</td>
                  <td className="py-2.5 text-right">{b.runsConceded}</td>
                  <td className="py-2.5 text-right font-semibold text-amber">{b.wickets}</td>
                  <td className="py-2.5 pr-4 text-right text-faint">{econ.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {inn.fallOfWickets.length > 0 && (
        <div className="border-t border-line px-4 py-3">
          <div className="mb-1.5 font-mono text-label uppercase tracking-[.14em] text-faint">
            Fall of wickets
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-caption">
            {inn.fallOfWickets.map((f, i) => (
              <span key={i} className="text-faint">
                <span className="text-chalk">{f.runs}-{f.wickets}</span> {name(f.playerId)} ({f.over})
              </span>
            ))}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

function how(kind?: string): string {
  if (!kind) return "out";
  return kind.replace(/_/g, " ").toLowerCase();
}

/* ── over by over ───────────────────────────────────────────────────────── */

export function OverByOver({ snap, name }: { snap: Snapshot; name: (id: string) => string }) {
  const per = snap.meta.ruleset.ballsPerOver;
  const overs = groupIntoOvers(snap.deliveries ?? [], per);
  if (overs.length === 0) {
    return <p className="px-4 py-10 text-center text-body text-faint">No balls bowled yet.</p>;
  }
  const points = overs.map((o) => ({ over: o.index + 1, runs: o.runs, wickets: o.wickets }));
  const rs = snap.meta.ruleset;

  return (
    <div className="px-3 pt-4 split:px-6">
      {/* Shape first: where the match turned is faster to see than to read. */}
      {points.length >= 2 && (
        <div className="mb-3 overflow-hidden rounded-card border border-line bg-panel p-4">
          <SectionLabel className="mb-2 block">Runs per over</SectionLabel>
          <Manhattan overs={points} />
          <SectionLabel className="mb-2 mt-4 block">Run rate</SectionLabel>
          <RunRate overs={points} target={snap.state.target}
                   totalOvers={rs.oversPerInnings} />
        </div>
      )}
      {overs.slice().reverse().map((ov) => (
        <div key={ov.index} className="mb-3 overflow-hidden rounded-xl border border-line bg-panel">
          <div className="flex items-center gap-3 border-b border-line px-4 py-2.5">
            <span className="rounded-md bg-raise px-2 py-1 font-mono text-meta font-semibold">
              OV {ov.index + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-body">{name(ov.bowlerId)}</span>
            <span className="font-mono text-body font-semibold">
              {ov.runs} run{ov.runs === 1 ? "" : "s"}
              {ov.wickets > 0 && <span className="ml-1.5 text-leather">{ov.wickets}w</span>}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 px-4 py-3">
            {ov.balls.map((ev, i) => {
              const d = ev.delivery, dr = ev.derived;
              const label = d.wicket ? "W"
                : d.wide ? `${dr.totalRuns}wd`
                : d.noBall ? `${dr.totalRuns}nb`
                : d.batBoundary === "SIX" ? "6" : d.batBoundary === "FOUR" ? "4"
                : dr.totalRuns === 0 ? "·" : String(dr.totalRuns);
              const tone = d.wicket ? "border-[#3A2422] bg-[#2A1614] text-leather"
                : d.batBoundary ? "border-[#4A3A1C] bg-[#20180C] text-amber"
                : (d.wide || d.noBall || d.byes || d.legByes) ? "border-line text-crease"
                : "border-line text-chalk";
              return (
                <span key={i}
                  className={`flex h-9 min-w-[36px] items-center justify-center rounded-lg border px-2
                              font-mono text-body font-semibold ${tone}`}>
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

interface Over { index: number; bowlerId: string; balls: RecordedDelivery[]; runs: number; wickets: number }

/** Group the delivery list into overs, counting only legal balls toward six. */
function groupIntoOvers(evs: RecordedDelivery[], per: number): Over[] {
  const out: Over[] = [];
  let cur: Over | null = null;
  let legal = 0;
  for (const ev of evs) {
    if (!cur) {
      cur = { index: out.length, bowlerId: ev.delivery.bowlerId, balls: [], runs: 0, wickets: 0 };
    }
    cur.balls.push(ev);
    cur.runs += ev.derived.totalRuns;
    if (ev.delivery.wicket) cur.wickets += 1;
    if (ev.derived.legal) legal += 1;
    if (legal >= per) { out.push(cur); cur = null; legal = 0; }
  }
  if (cur && cur.balls.length) out.push(cur);
  return out;
}


/* ── the commentary feed ────────────────────────────────────────────────────
   Ball by ball, with the calls a ground PA actually makes threaded in: the
   score read out at the end of every over, the target named at the innings
   break, and the result when it is done.                                    */

export interface FeedLine {
  key: string;
  over: string;
  text: string;
  kind: "ball" | "over" | "target" | "result" | "situation";
  /**
   * Which clip to play, decided by the delivery rather than by matching the
   * prose. Reading the sentence meant a quarter of balls played the wrong
   * audio, and every new commentary line could break it again.
   */
  bank?: string;
}

/**
 * Optional out-parameter carrying the situation as at the final ball.
 *
 * The between-balls commentary needs to know where the game is *now*. buildFeed
 * already derives exactly that on every delivery while walking the innings, so
 * reporting the last one costs nothing — and re-deriving it separately would be
 * a second implementation of the same reading, free to drift from this one.
 */
export interface FeedOut { situation?: Situation | null }

export function buildFeed(
  snap: Snapshot, name: (id: string) => string, lang: Lang = "en", report?: FeedOut,
): FeedLine[] {
  const per = snap.meta.ruleset.ballsPerOver;
  const evs = snap.deliveries ?? [];
  const zones = snap.meta.ruleset.bonusZones;
  const out: FeedLine[] = [];

  let legal = 0, overIdx = 0, overRuns = 0, overWkts = 0;
  let runs = 0, wkts = 0;
  let sinceBoundary = 0, sinceWicket = 999, dots = 0;
  let partnership = 0, partnershipBalls = 0;
  const first = snap.state.innings[0];
  const inningsOf = (n: number) => snap.state.innings[n];

  // Which innings each delivery belongs to, so the break lands in the right place.
  let innIdx = 0;
  let ballsThisInnings = 0;
  const legalTarget = (i: number) => (inningsOf(i)?.legalBalls ?? 0);

  evs.forEach((ev, i) => {
    const d = ev.delivery;
    const zone = d.bonusZoneId ? zones.find((z) => z.id === d.bonusZoneId) : undefined;
    const text = line(ev as never, {
      bowler: name(d.bowlerId), batter: name(d.strikerId), lang,
      // The ledger sequence: stable for the life of the ball, so its wording is
      // too. This is what stops the feed rewriting itself on every snapshot.
      seed: ev.sequence,
      ...(zone ? { zoneLabel: zone.label } : {}),
    });
    runs += ev.derived.totalRuns;
    overRuns += ev.derived.totalRuns;
    partnership += ev.derived.totalRuns;
    if (ev.derived.legal) { legal += 1; ballsThisInnings += 1; partnershipBalls += 1; }

    if (d.batBoundary) sinceBoundary = 0; else if (ev.derived.legal) sinceBoundary += 1;
    if (ev.derived.totalRuns === 0 && ev.derived.legal && !d.wicket) dots += 1; else dots = 0;
    if (d.wicket) {
      wkts += 1; overWkts += 1;
      sinceWicket = 0;
      partnership = 0; partnershipBalls = 0;   // a new pair starts from zero
    } else if (ev.derived.legal) {
      sinceWicket += 1;
    }

    out.push({
      key: `b${i}`,
      over: `${Math.floor(Math.max(legal - 1, 0) / per)}.${(Math.max(legal - 1, 0) % per) + 1}`,
      text, kind: "ball", bank: bankOf(d),
    });

    if (legal > 0 && legal % per === 0) {
      overIdx += 1;
      const inn = inningsOf(innIdx);

      // A word about where the match is, at the end of an over. Not every over
      // — a running commentary on the situation every six balls would be worse
      // than none — but often enough that it feels like someone is watching.
      if (overIdx % 2 === 0 || overIdx === 1) {
        const sit = situationAt(snap, innIdx, overIdx, runs, wkts, {
          sinceBoundary, sinceWicket, partnership, partnershipBalls, dots,
        });
        if (report) report.situation = sit;
        const say = sit ? situational(sit, lang) : null;
        if (say) out.push({ key: `s${i}`, over: "", kind: "situation", text: say });
      }

      out.push({
        key: `o${i}`,
        over: `${overIdx}`,
        kind: "over",
        text: overCall({
          over: overIdx, runs: overRuns, wickets: overWkts,
          score: runs, wicketsDown: wkts,
          bowler: name(d.bowlerId),
          batting: snap.meta.teams[inn?.battingTeamId ?? ""]?.name ?? "",
        }, lang),
      });
      overRuns = 0; overWkts = 0;
    }

    // Innings break: the target call, naming both sides.
    if (innIdx === 0 && first && ballsThisInnings >= legalTarget(0) && snap.state.innings.length > 1) {
      const second = inningsOf(1);
      if (second) {
        out.push({
          key: `t${i}`, over: "", kind: "target",
          text: targetCall(
            snap.meta.teams[first.battingTeamId]?.name ?? "",
            snap.meta.teams[second.battingTeamId]?.name ?? "",
            first.totalRuns, first.wickets,
            `${Math.floor(first.legalBalls / per)}.${first.legalBalls % per}`,
            first.totalRuns + 1, lang,
          ),
        });
        innIdx = 1; runs = 0; wkts = 0; legal = 0; overIdx = 0; ballsThisInnings = 0;
      }
    }
  });

  if (snap.state.complete && snap.state.result) {
    out.push({
      key: "res", over: "", kind: "result",
      text: resultCall(snap.state.result.summary,
        snap.meta.teams[snap.state.result.winnerTeamId ?? ""]?.name ?? null, lang),
    });
  }
  return out;
}

export interface VoiceClip {
  id: string; over: number; ball: number; seconds: number; who: string | null;
}

export function Feed({ snap, name, lang = "en", limit, voice = [] }: {
  snap: Snapshot; name: (id: string) => string; lang?: Lang; limit?: number;
  /** Anything the scorer said aloud, placed where it was said. */
  voice?: VoiceClip[];
}) {
  const all = buildFeed(snap, name, lang);
  const lines = (limit ? all.slice(-limit) : all).reverse();
  if (lines.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-panel">
      <h4 className="px-4 pb-1.5 pt-3 font-mono text-label uppercase tracking-[.14em] text-faint">
        Commentary
      </h4>

      {/* A human voice belongs above the generated lines: someone took the
          trouble to say it. */}
      {voice.length > 0 && (
        <div className="border-t border-line bg-raise px-4 py-2.5">
          <div className="mb-1.5 font-mono text-label uppercase tracking-[.14em] text-faint">
            Said aloud
          </div>
          <div className="flex flex-wrap gap-2">
            {voice.slice().reverse().slice(0, 8).map((v) => (
              <button key={v.id}
                onClick={() => {
                  void new Audio(`/api/v1/voice/${v.id}/stream`).play().catch(() => undefined);
                }}
                className="flex min-h-[38px] items-center gap-2 rounded-full border border-line
                           px-3 font-mono text-meta text-chalk hover:border-amber">
                <span className="text-amber">▶</span>
                {v.over}.{v.ball}
                <span className="text-faint">{v.seconds.toFixed(0)}s</span>
                {v.who && <span className="text-faint">{v.who.split(" ")[0]}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {lines.map((l) => {
        if (l.kind === "over") {
          return (
            <div key={l.key} className="border-t border-line bg-raise px-4 py-2.5">
              <span className="font-mono text-caption font-semibold text-crease">{l.text}</span>
            </div>
          );
        }
        if (l.kind === "situation") {
          return (
            <div key={l.key} className="border-t border-line px-4 py-2.5">
              <span className="text-body italic leading-snug text-faint">{l.text}</span>
            </div>
          );
        }
        if (l.kind === "target" || l.kind === "result") {
          return (
            <div key={l.key} className="border-t border-line bg-[#20180C] px-4 py-3">
              <span className="font-mono text-body font-semibold text-amber">{l.text}</span>
            </div>
          );
        }
        const big = /^(FOUR|SIX)/.test(l.text);
        const wkt = /(WICKET|BOWLED|CAUGHT|LBW|RUN OUT|STUMPED|HIT WICKET|BOLD|CATCH|STUMP)/.test(l.text);
        return (
          <div key={l.key} className="flex gap-3 border-t border-line px-4 py-2.5">
            <span className="w-9 shrink-0 font-mono text-caption text-faint">{l.over}</span>
            <span className={`flex-1 text-body leading-snug
              ${wkt ? "font-semibold text-leather" : big ? "font-semibold text-amber" : ""}`}>
              {l.text}
            </span>
          </div>
        );
      })}
    </div>
  );
}


/** Build the situation the commentary layer reads, from the running feed state. */
function situationAt(
  snap: Snapshot, innIdx: number, over: number, runs: number, wkts: number,
  m: { sinceBoundary: number; sinceWicket: number; partnership: number;
       partnershipBalls: number; dots: number },
): Situation | null {
  const inn = snap.state.innings[innIdx];
  const rs = snap.meta.ruleset;
  if (!inn) return null;

  const per = rs.ballsPerOver;
  const total = rs.oversPerInnings;
  const balls = over * per;
  const runRate = balls ? runs / (balls / per) : 0;

  const target = innIdx > 0 ? snap.state.target : null;
  const need = target !== null ? target - runs : null;
  const ballsLeft = total * per - balls;
  const required = need !== null && ballsLeft > 0 ? need / (ballsLeft / per) : null;

  // A six-over match is all death overs; a twenty-over one has a middle. Scale
  // the phases rather than hard-coding over numbers.
  const phase: Situation["phase"] =
    over <= Math.max(1, Math.floor(total * 0.25)) ? "opening"
    : over >= Math.floor(total * 0.75) ? "death"
    : "middle";

  return {
    phase, over, totalOvers: total, runs, wickets: wkts,
    maxWickets: (rs as unknown as { maxWickets?: number }).maxWickets ?? 10,
    runRate, need, ballsLeft: ballsLeft > 0 ? ballsLeft : null, required,
    sinceBoundary: m.sinceBoundary, sinceWicket: m.sinceWicket,
    partnership: m.partnership, partnershipBalls: m.partnershipBalls, dots: m.dots,
    battingTeam: snap.meta.teams[inn.battingTeamId]?.name ?? "",
    bowlingTeam: snap.meta.teams[inn.bowlingTeamId]?.name ?? "",
  };
}

